import Swiper from "swiper";
import { Autoplay, Pagination } from "swiper/modules";

const AUTOPLAY_MS = 6000;
const MOVE_MS = 300;
const mobile = window.matchMedia("(max-width: 767px)");
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

document.querySelectorAll(".disciplines_list-layout").forEach((layout) => {
	const tabs = [...layout.querySelectorAll('[role="tab"]')];
	const panels = [...layout.querySelectorAll(".discipline-panel")];
	const rail = layout.querySelector(".disciplines-rail");
	if (panels.length < 2) return;

	let swiper = null;
	let current = 0;
	let autoplay = false;
	let inView = false;
	let animation = null;

	// De oranje lijn loopt vanaf de bovenkant van de lijst tot de onderkant van de actieve tab.
	const topOf = (i) => tabs[i].offsetTop;
	const bottomOf = (i) => tabs[i].offsetTop + tabs[i].offsetHeight;

	// Zet de lijn op een hoogte; `animate` schuift er in MOVE_MS naartoe.
	const moveRail = (to, duration) => {
		animation?.cancel();
		const from = rail.getBoundingClientRect().height;
		rail.style.height = `${to}px`;
		if (!duration || reduceMotion.matches) return Promise.resolve();
		animation = rail.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration, easing: "ease-in-out" });
		return animation.finished.catch(() => {});
	};

	// Autoplay: de lijn vult de actieve tab in AUTOPLAY_MS; daarna volgt de volgende tab.
	const runProgress = () => {
		animation?.cancel();
		const from = topOf(current);
		const to = bottomOf(current);
		rail.style.height = `${to}px`;
		animation = rail.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: AUTOPLAY_MS, easing: "linear" });
		if (!inView || document.hidden) animation.pause();
		animation.finished.then(
			() => autoplay && activate((current + 1) % tabs.length),
			() => {},
		);
	};

	const activate = (index, focus = false) => {
		current = index;
		tabs.forEach((tab, i) => {
			const selected = i === index;
			tab.setAttribute("aria-selected", String(selected));
			tab.tabIndex = selected ? 0 : -1;
			if (selected && focus) tab.focus();
		});
		panels.forEach((panel, i) => panel.classList.toggle("is-active", i === index));

		if (autoplay) {
			// Eerst naar de bovenkant van de nieuwe tab, daarna vult de lijn die tab.
			moveRail(topOf(index), MOVE_MS).then(() => autoplay && current === index && runProgress());
		} else {
			moveRail(bottomOf(index), MOVE_MS);
		}
	};

	const syncPlaying = () => {
		if (!autoplay || !animation || animation.playState === "finished") return;
		if (inView && !document.hidden) animation.play();
		else animation.pause();
	};

	// Elke handeling van de bezoeker stopt de autoplay voorgoed; de lijn loopt dan meteen vol.
	const stopAutoplay = () => {
		if (!autoplay) return;
		autoplay = false;
		moveRail(bottomOf(current), MOVE_MS);
	};

	tabs.forEach((tab, i) => {
		tab.addEventListener("click", () => activate(i));
		tab.addEventListener("keydown", (e) => {
			const keys = { ArrowDown: i + 1, ArrowRight: i + 1, ArrowUp: i - 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 };
			if (!(e.key in keys)) return;
			e.preventDefault();
			activate((keys[e.key] + tabs.length) % tabs.length, true);
		});
	});
	["pointerdown", "keydown", "focusin"].forEach((type) => layout.addEventListener(type, stopAutoplay));

	new IntersectionObserver(
		([entry]) => {
			inView = entry.isIntersecting;
			syncPlaying();
			if (swiper?.autoplay) {
				if (inView && swiper.autoplay.running) swiper.autoplay.resume();
				else swiper.autoplay.pause();
			}
		},
		{ threshold: 0.4 },
	).observe(layout);
	document.addEventListener("visibilitychange", syncPlaying);

	// Zonder animatie volgt de lijn de lettergrootte mee als het venster verandert.
	window.addEventListener("resize", () => {
		if (!mobile.matches && !autoplay) rail.style.height = `${bottomOf(current)}px`;
	});

	// Op mobiel zijn de panelen een slider; de tabs bestaan daar niet.
	const sync = () => {
		if (mobile.matches && !swiper) {
			autoplay = false;
			animation?.cancel();
			panels.forEach((panel) => panel.removeAttribute("role"));
			swiper = new Swiper(layout.querySelector(".disciplines-panels"), {
				modules: [Autoplay, Pagination],
				wrapperClass: "panels-wrapper",
				slideClass: "discipline-panel",
				slideActiveClass: "is-active",
				slidesPerView: 1,
				// De hoogte volgt de actieve slide: een slide met minder tekst laat anders een gat tussen de knop en de dots.
				autoHeight: true,
				centeredSlides: true,
				spaceBetween: 8,
				autoplay: reduceMotion.matches ? false : { delay: AUTOPLAY_MS, disableOnInteraction: true },
				pagination: { el: layout.querySelector(".pk-pagination"), clickable: true },
			});
			if (swiper.autoplay?.running && !inView) swiper.autoplay.pause();
		} else if (!mobile.matches) {
			if (swiper) {
				swiper.destroy(true, true);
				swiper = null;
				panels.forEach((panel) => panel.setAttribute("role", "tabpanel"));
			}
			autoplay = !reduceMotion.matches;
			activate(0);
		}
	};

	sync();
	mobile.addEventListener("change", sync);
});
